import { isActionAllowed } from '@/helpers/rights.js';
import { User } from 'oidc-client-ts';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { keycloak } from '../config.json';

@Module({ namespaced: true })
export default class Auth extends VuexModule {
  _currentUser: any = null;

  get currentUsername() {
    return this._currentUser ? this._currentUser.content.username : null;
  }

  get currentUserRights() {
    return this._currentUser ? this._currentUser.rights : null;
  }

  get currentUser() {
    return this._currentUser;
  }

  @Mutation
  SET_CURRENT_USER(user: any) {
    this._currentUser = user;
  }

  @Mutation
  UNSET_CURRENT_USER() {
    this._currentUser = null;
  }

  @Action
  async LOGIN(payload: {
    kuzzle: any;
    strategy: string;
    user: User;
    visitorId: number;
  }) {
    let jwt;
    try {
      const response = await payload.kuzzle.auth.query({
        action: 'login',
        strategy: payload.strategy,
        visitorId: payload.visitorId,
        body: {
          access_token: payload.user.access_token,
          id_token: payload.user.id_token,
        },
      });

      if (response.status !== 200) {
        return response.error.message;
      }

      jwt = response.result.jwt;
      payload.kuzzle.jwt = jwt;

      await this.context.dispatch('FETCH_CURRENT_USER', payload.kuzzle);
    } catch (error) {
      localStorage.removeItem('user_token');
      throw error;
    }

    localStorage.setItem('user_token', jwt);
  }

  @Action
  async CHECK_TOKEN(kuzzle: any) {
    const jwt = localStorage.getItem('user_token');

    if (!jwt) {
      return false;
    }

    const { valid } = await kuzzle.auth.checkToken(jwt);

    if (!valid) {
      await this.context.dispatch('LOG_OUT', kuzzle);
      return false;
    }

    kuzzle.jwt = jwt;

    if (!this.currentUser) {
      await this.context.dispatch('FETCH_CURRENT_USER', kuzzle);
    }

    return true;
  }

  @Action
  async FETCH_CURRENT_USER(kuzzle: any) {
    let currentUser;

    try {
      const [credentials, rights, { content }] = await Promise.all([
        kuzzle.auth.getMyCredentials(keycloak.strategy),
        kuzzle.auth.getMyRights(),
        kuzzle.auth.getCurrentUser(),
      ]);

      currentUser = credentials;
      currentUser.rights = rights;
      currentUser.content = content;
      if (isActionAllowed(rights, 'multi-tenancy/tenant', 'list')) {
        await this.context.dispatch('tenants/FETCH_TENANT_LIST', kuzzle, {
          root: true,
        });
      } else {
        await this.context.dispatch('tenants/FETCH_USER_TENANTS', kuzzle, {
          root: true,
        });
      }
    } catch (error) {
      throw error;
    }

    this.context.commit('SET_CURRENT_USER', currentUser);
  }

  @Action
  async LOG_OUT(payload: {
    kuzzle: any;
    visitorId: number;
    actionName: string;
  }) {
    /**
     * You should tear down your session here.
     */
    this.context.commit('UNSET_CURRENT_USER');

    await payload.kuzzle.auth.query({
      action: 'logout',
      actionName: payload.actionName,
      visitorId: payload.visitorId,
    });

    payload.kuzzle.jwt = null;
    localStorage.removeItem('user_token');
  }
}
